"use client";

import { resolveFormFillAccent } from "@/app/(dashboard)/_components/forms/fill/form-fill-accent";
import Link from "next/link";
import type { ReactElement, ReactNode } from "react";
import "./login.css";
import { useNativeAppKind } from "@/lib/native-app/native-app-context";
import {
  PORTAL_SIGN_IN_PATH,
  STAFF_SIGN_IN_PATH,
} from "@/lib/portal-sign-in-path";
import { useTranslations } from "next-intl";
import { DotGridBackdrop } from "../../_components/marketing/dot-grid-backdrop";
import { ACCENT, CARD_BG, INK, PAGE_BG } from "./login-shared";
import { BrandLockup, BrandLockupMark } from "./login-welcome-panel";
import { PortalBrandLockup } from "./portal-brand-lockup";
import type { PortalLoginBranding } from "./portal-login-screen";

const FONT_STACK =
  "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif";

type AuthPageShellProps = {
  readonly title: string;
  readonly description?: string;
  readonly backHref?: string;
  readonly backLabel?: string;
  /** Body slot — omitted by loading-fallback callers that only show the header. */
  readonly children?: ReactNode;
  /** Portal host → hide hausverwalter header and use org branding. */
  readonly portalHost?: boolean;
  readonly portalBranding?: PortalLoginBranding | null;
};

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  const n = match?.[1] ? Number.parseInt(match[1], 16) : 0x008070;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function AuthCardContent({
  title,
  description,
  backHref,
  backLabel: backLabelProp,
  children,
  portalHost = false,
  portalBranding = null,
  accent,
}: AuthPageShellProps & { readonly accent: string }): ReactElement {
  const t = useTranslations("public.authPageShell");
  const backLabel = backLabelProp ?? t("zurueckLogin");
  const isBrandedOrg = portalBranding?.org_name != null;
  const linkAccent = portalHost ? accent : ACCENT;
  const resolvedBackHref =
    backHref ?? (portalHost ? PORTAL_SIGN_IN_PATH : STAFF_SIGN_IN_PATH);

  return (
    <>
      {portalHost && (isBrandedOrg || portalBranding?.logo_url) ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            marginBottom: 20,
          }}
        >
          <PortalBrandLockup
            branding={
              portalBranding ?? {
                org_name: null,
                logo_url: null,
                brand_color: null,
              }
            }
            accent={accent}
          />
        </div>
      ) : null}

      {/* Unbranded portal hosts get a "Kundenportal" label — without an org
          logo it is the only hint whose portal this is. Our own auth screens
          carry no eyebrow: the heading already says it. */}
      {portalHost && !isBrandedOrg ? (
        <div
          style={{
            fontFamily:
              "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif",
            fontSize: 10,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "rgba(11,15,25,0.45)",
            textAlign: "center",
          }}
        >
          {t("kundenportal")}
        </div>
      ) : null}

      <h1
        style={{
          fontWeight: 600,
          fontSize: 26,
          letterSpacing: "-0.02em",
          color: INK,
          margin: portalHost && !isBrandedOrg ? "11px 0 0" : 0,
          textAlign: portalHost ? "center" : undefined,
        }}
      >
        {title}
      </h1>
      {description ? (
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.5,
            color: "rgba(11,15,25,0.55)",
            margin: "8px 0 0",
            textAlign: portalHost ? "center" : undefined,
          }}
        >
          {description}
        </p>
      ) : null}

      {children}

      {resolvedBackHref ? (
        <div style={{ marginTop: 24, textAlign: "center" }}>
          <Link
            href={resolvedBackHref}
            className="mklogin-link"
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: linkAccent,
              textDecoration: "none",
            }}
          >
            {backLabel}
          </Link>
        </div>
      ) : null}
    </>
  );
}

export function AuthPageShell(props: AuthPageShellProps): ReactElement {
  const t = useTranslations("public.authPageShell");
  const nativeApp = useNativeAppKind();
  const { portalHost = false, portalBranding = null } = props;
  const accent = resolveFormFillAccent(portalBranding?.brand_color ?? "");
  const { r, g, b } = hexToRgb(accent);

  if (portalHost) {
    return (
      <div
        className="mklogin"
        style={{
          position: "relative",
          minHeight: "100dvh",
          boxSizing: "border-box",
          fontFamily: FONT_STACK,
          background: PAGE_BG,
          color: INK,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 16px",
          overflowX: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            backgroundImage: `radial-gradient(60% 50% at 12% 0%, rgba(${r},${g},${b},0.12) 0%, transparent 60%), radial-gradient(54% 56% at 100% 100%, rgba(${r},${g},${b},0.08) 0%, transparent 62%)`,
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: 440,
            background: CARD_BG,
            border: "1px solid rgba(11,15,25,0.10)",
            borderRadius: 20,
            boxShadow:
              "0 40px 90px -45px rgba(11,15,25,0.34), 0 12px 30px -16px rgba(11,15,25,0.14)",
            padding: "36px 32px 32px",
            boxSizing: "border-box",
          }}
        >
          <AuthCardContent {...props} accent={accent} />
        </div>

        <p
          style={{
            position: "relative",
            zIndex: 1,
            marginTop: 22,
            fontSize: 12,
            color: "rgba(11,15,25,0.4)",
            textAlign: "center",
          }}
        >
          {t("bereitgestelltUeberHausverwalterAi")}
        </p>
      </div>
    );
  }

  return (
    <div
      className="mklogin"
      style={{
        minHeight: "100dvh",
        fontFamily: FONT_STACK,
        background: PAGE_BG,
        color: INK,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      <DotGridBackdrop tone="light" focus="top" />

      <header
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 480,
          margin: "0 auto",
          height: 74,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 24px",
          boxSizing: "border-box",
        }}
      >
        {nativeApp ? (
          <BrandLockupMark variant="light" />
        ) : (
          <>
            <BrandLockup variant="light" />
            <Link
              href="/"
              className="mklogin-website-link"
              style={{
                fontSize: 13.5,
                fontWeight: 500,
                color: "rgba(11,15,25,0.6)",
                textDecoration: "none",
              }}
            >
              {t("website")}
            </Link>
          </>
        )}
      </header>

      <main
        style={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px 24px 48px",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 420,
            background: CARD_BG,
            border: "1px solid rgba(11,15,25,0.10)",
            borderRadius: 18,
            boxShadow:
              "0 18px 40px -24px rgba(11,15,25,0.26), 0 4px 12px -7px rgba(11,15,25,0.12)",
            padding: "36px 32px 32px",
            boxSizing: "border-box",
          }}
        >
          <AuthCardContent {...props} accent={accent} />
        </div>
      </main>
    </div>
  );
}
